/**
 * Allows distinguishing between calls with no arguments and calls with `undefined`.
 */
const NO_ARG_KEY = Symbol("knyt.artisan.noArgKey");
/**
 * Memoizes the result of a function with a single argument using strict equality.
 *
 * @remarks
 *
 * The cache will only store the last `maxCacheSize` results.
 * This function is designed to be as simple as possible,
 * and should be used with pure functions that are expensive to compute.
 *
 * **Functions with rest parameters are not supported.**
 *
 * @internal scope: package
 */
export function memoizeOne(fn, maxCacheSize = 1) {
    const cache = new Map();
    return function (payload) {
        const cacheKey = arguments.length === 0 ? NO_ARG_KEY : payload;
        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }
        if (cache.size >= maxCacheSize) {
            const lastKey = cache.keys().next().value;
            if (lastKey) {
                cache.delete(lastKey);
            }
        }
        const result = fn.call(this, payload);
        cache.set(cacheKey, result);
        return result;
    };
}
/**
 * Memoizes the result of a function with any number of arguments.
 *
 * @remarks
 *
 * The cache will only store the last `maxCacheSize` results.
 * Uses a simple array of argument references as the cache key.
 * Only use with primitive or reference-stable arguments.
 *
 * @internal scope: package
 */
export function memoizeMany(fn, maxCacheSize = 1) {
    const cache = [];
    return function (...args) {
        // Find a cache entry with the same arguments (by reference or primitive equality)
        // TODO: Benchmark performance for large caches and consider using a Map of Maps for better performance
        for (const entry of cache) {
            if (entry.args.length === args.length &&
                entry.args.every((v, i) => v === args[i])) {
                return entry.result;
            }
        }
        if (cache.length >= maxCacheSize) {
            cache.shift();
        }
        const result = fn.apply(this, args);
        cache.push({ args: [...args], result });
        return result;
    };
}
/**
 * Memoizes the result of a function with any number of arguments.
 *
 * @remarks
 *
 * Chooses between `memoizeOne` and `memoizeMany` based on the number of arguments.
 * The cache will only store the last `maxCacheSize` results.
 *
 * **Functions with rest parameters are not supported.**
 *
 * @public
 */
export function memoize(fn, maxCacheSize = 1) {
    if (fn.length <= 1) {
        return memoizeOne(fn, maxCacheSize);
    }
    return memoizeMany(fn, maxCacheSize);
}
